// import { selectThemeColors } from "@helpers/utils";
// ** Reactstrap
import { MultiValue, OptionsContainer } from "../components";
// ** Custom Components
import { REACT_STYLES, STATUS_ALL } from "@src/utility/constants";
import { getStatusBadge, selectThemeColors } from "@helpers/utils";
import { useEffect, useRef, useState } from "react";

import Avatar from "@components/ui-components/avatar";
import CustomTooltip from "@components/ui-components/tooltip";
import { Label } from "reactstrap";
import PropTypes from "prop-types";
// ** Third Party
import ReactSelect from "react-select";
import classnames from "classnames";
import size from "lodash/size";
import { useTranslation } from "react-i18next";

const RawSelectField = props => {
    const {
        label = null,
        errors = {},
        name = null,
        options,
        isMulti = false,
        disabled = false,
        id = null,
        loading = false,
        isClearable = false,
        customOnChange = null,
        selectAll = false,
        isDirtyField = false,
        groupedOptions = null,
        placeholder = null,
        className = "",
        value = null,
        onChange,
        onBlur = () => {},
        isInvalid = false,
        maxAllowedSelection = null,
        required = false,
        tooltipContent = null,
        desc = null,
        message = null,
        showErrMsg = true,
        minRestriction = false,
        onInputChange
    } = props;
    // To trigger focus
    const selectRef = useRef();

    const [isTourActive] = useState(false);
    
    const maxSelectedAllowed =
    maxAllowedSelection || (size(options) > 10 ? 10 : undefined);
    const [showMaxSelectedWarning, setShowMaxSelectedWarning] = useState(false);
    const selectAllOption = {
        label: maxSelectedAllowed ? `Select top ${maxSelectedAllowed} ` : "Select All",
        value: STATUS_ALL
    };

    const { t } = useTranslation();
    // If selectAll is true, push Select All field else original options.
    const getAllOptions = () => {
        const hasGroupedOptions = groupedOptions;
        const isMultiAndSelectAll = isMulti && selectAll;
        const valueSize = size(value);

        if (isMultiAndSelectAll) {
            const optionsToCheckSize = hasGroupedOptions ? options.map(acc => acc.options).flat(1) : options;

            if (valueSize !== size(optionsToCheckSize)) {
                const additionalOptions = hasGroupedOptions ? [{ label: "All", options: [selectAllOption] }] : [selectAllOption];
                return [...additionalOptions, ...props.options];
            }
        }

        return props.options.filter(item => !item.omit);
    };

    const handleShowWarning = (selectedOptions) => {
        if (maxSelectedAllowed && isMulti) {
            if (
                size(selectedOptions) >= maxSelectedAllowed 
            ) {
                options.forEach(item => {
                    const index = selectedOptions.findIndex(
                        obj => obj.value || obj === item.value
                    );
                    item.isDisabled = index < 0;
                });
            } else {
                options.forEach(item => {
                    item.isDisabled = false;
                });
            }
        }
        if (maxSelectedAllowed && size(selectedOptions) >= maxSelectedAllowed && isMulti) {
            setShowMaxSelectedWarning(true);
            selectRef?.current.blur();
        } else {
            setShowMaxSelectedWarning(false);
        }
    };

    /**
   * A function that takes in two parameters, options and onChange. It then checks if the isMulti
   * variable is true. If it is, it will map over the options and return the value of each option. If
   * it is not true, it will return the value of the option.
   */
    const handleOnChange = selectedOptions => {
        if (isMulti && selectAll) {
            const isSelectAll = selectedOptions.some(
                option => option.value === selectAllOption.value
            );

            if (isSelectAll) {
                const selectedValues = groupedOptions ? options
                    .map(acc => acc.options)
                    .flat(1)
                    .map(option => option.value)
                    .slice(0, maxSelectedAllowed) : options.slice(0, maxSelectedAllowed);
                handleOnChange(selectedValues);
                return;
            }
        }

        const selectedValues = isMulti ? selectedOptions?.map(option => option.value) : selectedOptions?.value || "";
        if (!selectedValues.length && minRestriction) {
            // No option selected, select at least one
            const firstOptionValue = options[0]?.value;
            if (firstOptionValue) {

                onChange([firstOptionValue]);
            } else {
                // Handle the case when there are no options available
                console.error("No options available to select.");
            }
        } else if (isMulti && maxSelectedAllowed && size(selectedOptions) > maxSelectedAllowed) {
            handleShowWarning(selectedOptions);
        } else {
            onChange(selectedValues);
        }
       
    };

    /**
   * It returns the value of the selected option
   * @returns The value of the option that is selected.
   */
    const handleValue = value => {
        if (isMulti) {
            if (groupedOptions) {
                return options
                    .map(acc => acc.options)
                    .flat(1)
                    .filter(item => value.includes(item.value));
            }
            return options.filter(option => value?.includes(option.value));
        } else {
            if (groupedOptions) {
                return (
                    options
                        .map(acc => acc.options)
                        .flat(1)
                        ?.find(c => c?.value === value) || null
                );
            }
            return options?.find(c => c?.value === value) || null;
        }
    };

    const components = {
        Option: OptionsContainer,
        MultiValue
    };

    const filterOptions = (option, inputValue) => {
        return option.label?.props?.children[1]
            .toLowerCase()
            ?.includes(inputValue.toLowerCase());
    };

    const renderOptionLabel = option => {
        return (
            <>
                {option.icon || option.image || option.status ? (
                    <span className="me-75">
                        {option.icon?.type ? (
                            option.icon
                        ) : (
                            <Avatar
                                img={option.image}
                                icon={option.icon}
                                className="me-75"
                                color={
                                    option.icon ? "secondary" : getStatusBadge(option.status)?.color
                                }
                                content={option.icon ? "" : option.label}
                                rounded
                                initials={!option.image}/>
                        )}
                    </span>
                ) : null}
                {t(option.label)}
            </>
        );
    };
    /**
   * If there is an error message, return the error message
   * @returns message
   */
    const renderError = () => {
        return (
            message && (
                <small className="text-danger" data-cy={`error-${id}`}>
                    {message}
                </small>
            )
        );
    };

    useEffect(() => {
        handleShowWarning(value);
    }, [value]);

    return (
        <div
            data-tour={`select-${id}`}
            id={`error-${name?.replaceAll(".", "-")}`}
            className={classnames({
                "mb-1"         : !className,
                [className]    : className,
                "single-select": !isMulti,
                "tour-active"  : isTourActive
            })}>
            {label && (
                <Label htmlFor={name} data-cy={`label-${id}`}>
                    {t(label)}
                    {required && <span className="text-danger fw-bolder">*</span>}
                    {Boolean(tooltipContent) && (
                        <>
                            <i
                                className="mdi mdi-information-outline ms-75"
                                id={`tooltip-select-${id}`}/>
                            <CustomTooltip
                                target={`tooltip-select-${id}`}
                                content={tooltipContent}/>
                        </>
                    )}
                </Label>
            )}
            <ReactSelect
                ref={selectRef}
                id={`select-${id}`} 
                name={name}
                isClearable={isClearable}
                isMulti={isMulti}
                components={components}
                closeMenuOnSelect={!isMulti}
                options={getAllOptions()}
                onBlur={onBlur}
                styles={REACT_STYLES({
                    type: isInvalid ? "error" : showMaxSelectedWarning ? "warning" : ""
                })}
                theme={selectThemeColors}
                errors={errors}
                className={classnames("react-select", {
                    "is-dirty-field": isDirtyField && !isInvalid
                    // [selectClass]   : selectClass
                })}
                onInputChange={onInputChange}
                noOptionsMessage={() => "Sorry, no matching options."}
                classNamePrefix="select"
                onChange={selectedOptions => {
                    handleOnChange(selectedOptions);
                    if (customOnChange) {
                        customOnChange(selectedOptions);
                    }
                }}
                filterOption={filterOptions}
                getOptionLabel={renderOptionLabel}
                placeholder={
                    placeholder ? t(placeholder) : `${t("Select")} ${t(label)}`
                }
                hideSelectedOptions={false}
                escapeClearsValue={false}
                value={handleValue(value)}
                isLoading={loading}
                isDisabled={disabled}/>
            {desc && (
                <p>
                    <small className="fw-bold" data-cy="select-desc">
                        <i>{t(desc)}</i>
                    </small>
                </p>
            )}

            {showMaxSelectedWarning ? (
                <p>
                    <small className="text-warning">
                        {t("Max {{maxSelectedAllowed}} can be selected", {
                            maxSelectedAllowed
                        })}
                    </small>
                </p>
            ) : null}
            {isInvalid && !showErrMsg && message && (
                <CustomTooltip
                    target={`error-${name.replaceAll(".", "-")}`}
                    content={message}/>
            )}
            {showErrMsg && renderError()}
        </div>
    );
};

RawSelectField.propTypes = {
    label              : PropTypes.string,
    errors             : PropTypes.object,
    name               : PropTypes.string,
    options            : PropTypes.array.isRequired,
    isMulti            : PropTypes.bool,
    disabled           : PropTypes.bool,
    id                 : PropTypes.string,
    loading            : PropTypes.bool,
    isClearable        : PropTypes.bool,
    customOnChange     : PropTypes.func,
    selectAll          : PropTypes.bool,
    isDirtyField       : PropTypes.bool,
    groupedOptions     : PropTypes.array,
    placeholder        : PropTypes.string,
    className          : PropTypes.string,
    value              : PropTypes.any,
    onChange           : PropTypes.func.isRequired,
    onBlur             : PropTypes.func,
    isInvalid          : PropTypes.bool,
    maxAllowedSelection: PropTypes.number,
    required           : PropTypes.bool,
    tooltipContent     : PropTypes.string,
    desc               : PropTypes.string,
    message            : PropTypes.string,
    showErrMsg         : PropTypes.bool,
    minRestriction     : PropTypes.bool,
    onInputChange      : PropTypes.func
};

export default RawSelectField;
